import React from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import Auth from "@/pages/Auth";
import AppLayout from "@/components/AppLayout";
import { Loader2 } from "lucide-react";

// Protected Route wrapper
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const {
    isAuthenticated,
    isLoading,
    loginWithGoogle,
    loginWithToken,
    loginDemo,
  } = useAuth();

  // Check for OAuth callback parameters
  React.useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const authenticated = urlParams.get("authenticated");
    const provider = urlParams.get("provider");
    const token = urlParams.get("token");
    const email = urlParams.get("email");

    if (
      authenticated === "true" &&
      provider === "google" &&
      token &&
      email &&
      !isAuthenticated
    ) {
      // User returned from Google OAuth - complete authentication
      console.log("🔐 Processing OAuth callback with email:", email);

      // Use the token-based login instead of mock credentials
      loginWithToken(decodeURIComponent(token), decodeURIComponent(email))
        .then(() => {
          console.log("✅ OAuth login successful");
          // Clean up URL parameters
          window.history.replaceState(
            {},
            document.title,
            window.location.pathname,
          );
        })
        .catch((error) => {
          console.error("❌ OAuth login failed:", error);
          // In production, do NOT fall back to demo
          if ((import.meta as any).env?.PROD) {
            // Leave unauthenticated; App will route to /auth
            return;
          }
          // Dev convenience: fallback to demo locally only
          loginDemo();
        });
    }
  }, [isAuthenticated, loginWithGoogle, loginWithToken, loginDemo]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background dark:bg-gray-900 transition-colors">
        <div className="text-center">
          <div className="relative">
            <div className="absolute inset-0 blur-xl bg-primary/20 rounded-full animate-pulse" />
            <Loader2 className="relative w-12 h-12 animate-spin mx-auto mb-4 text-primary" />
          </div>
          <p className="text-sm font-medium text-foreground dark:text-gray-200 mt-2">Loading...</p>
          <p className="text-xs text-muted-foreground dark:text-gray-400 mt-1">Preparing your workspace</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/auth" replace />;
  }

  return <>{children}</>;
}

// Public Route wrapper (only accessible when not authenticated)
function PublicRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background dark:bg-gray-900 transition-colors">
        <div className="text-center">
          <div className="relative">
            <div className="absolute inset-0 blur-xl bg-primary/20 rounded-full animate-pulse" />
            <Loader2 className="relative w-12 h-12 animate-spin mx-auto mb-4 text-primary" />
          </div>
          <p className="text-sm font-medium text-foreground dark:text-gray-200 mt-2">Loading...</p>
          <p className="text-xs text-muted-foreground dark:text-gray-400 mt-1">Preparing your workspace</p>
        </div>
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

function AppRoutes() {
  return (
    <Routes>
      {/* Public routes */}
      <Route
        path="/auth"
        element={
          <PublicRoute>
            <Auth />
          </PublicRoute>
        }
      />

      {/* Protected routes */}
      <Route
        path="/*"
        element={
          <ProtectedRoute>
            <AppLayout />
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppRoutes />
    </AuthProvider>
  );
}

export default App;
